import { useCallback, useMemo, useRef } from 'react';

import isNil from '../../../../utils/isNil';
import { SELECT_ALL_OPTION_KEY, SELECT_DEFAULT_OPTION_KEY_PROP } from '../../Select.constants';
import { getOptionIdentifier, mapOptionsToIdentifiers } from '../../Select.helpers';

import type { SelectOptionsReturn } from './useSelectOptions';
import type { InternalSelectOption, SelectOption, SelectRootProps } from '../../Select.types';

type OptionsSelectionProps = {
    setInternalSearchValue: React.Dispatch<React.SetStateAction<string>>;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    options: SelectOptionsReturn;
} & Pick<
    SelectRootProps,
    'mode' | 'onChange' | 'onSelect' | 'onDeselect' | 'optionKeyProp' | 'value'
>;

export const useSelectSelection = ({
    mode,
    onChange,
    onSelect,
    onDeselect,
    optionKeyProp = SELECT_DEFAULT_OPTION_KEY_PROP,
    value,
    setInternalSearchValue,
    setOpen,
    options
}: OptionsSelectionProps) => {
    const lastOnChangeValue = useRef(value);

    const enabledOptions = useMemo(
        () => options.flattenedOptions.filter((opt) => !opt.disabled),
        [options.flattenedOptions]
    );
    const allEnabledOptionsIdentifiers = useMemo(
        () => mapOptionsToIdentifiers(enabledOptions, optionKeyProp),
        [enabledOptions, optionKeyProp]
    );
    const areAllSelected =
        mode === 'multiple' &&
        Array.isArray(value) &&
        value.length === allEnabledOptionsIdentifiers.length;

    const handleSelectAll = useCallback(() => {
        if (!areAllSelected) {
            onChange?.(allEnabledOptionsIdentifiers, enabledOptions);
        } else {
            onChange?.([], []);
        }
    }, [onChange, allEnabledOptionsIdentifiers, enabledOptions, areAllSelected]);

    const mergedOnChange = useCallback(
        (newValue: string | null | undefined, internalOption?: InternalSelectOption) => {
            const optionValue = internalOption
                ? getOptionIdentifier(internalOption, optionKeyProp)
                : newValue ?? undefined;
            const option = (internalOption?.original ?? internalOption) as SelectOption;

            if (mode === 'multiple') {
                if (newValue?.includes(SELECT_ALL_OPTION_KEY)) {
                    handleSelectAll();
                } else {
                    const isDeselection = value?.includes(optionValue);
                    const newSelectedValues = isDeselection
                        ? value.filter((v: string) => v !== optionValue)
                        : [...(value ?? []), optionValue];
                    const newSelectedOptions = options.flattenedOptions.filter((opt) => {
                        const optValue = getOptionIdentifier(opt, optionKeyProp);
                        return newSelectedValues.includes(optValue);
                    });
                    onChange?.(newSelectedValues, newSelectedOptions);
                    if (isDeselection) {
                        onDeselect?.(optionValue, option);
                    } else {
                        onSelect?.(optionValue, option);
                    }
                }
                return;
            }
            onChange?.(newValue, option);
            onSelect?.(newValue, option);
            lastOnChangeValue.current = newValue;
        },
        [
            onChange,
            onSelect,
            onDeselect,
            optionKeyProp,
            mode,
            value,
            options.flattenedOptions,
            handleSelectAll
        ]
    );

    const handleSelect = useCallback(
        (selectedOption: InternalSelectOption) => {
            if (selectedOption.disabled) {
                return;
            }
            const selectedValue = getOptionIdentifier(selectedOption, optionKeyProp);
            mergedOnChange?.(selectedValue, selectedOption);
            if (mode === 'single') {
                setInternalSearchValue('');
                setOpen(false);
            }
        },
        [mergedOnChange, optionKeyProp, mode, setInternalSearchValue, setOpen]
    );

    const getOptionByIndex = useCallback(
        (index: number) => {
            if (isNil(index) || index >= options.displayOptions.length) {
                return null;
            }
            return options.displayOptions[index] as InternalSelectOption;
        },
        [options.displayOptions]
    );

    const selectOptionByIndex = useCallback(
        (index: number) => {
            const option = getOptionByIndex(index);
            if (!option) {
                return;
            }
            handleSelect(option);
        },
        [getOptionByIndex, handleSelect]
    );

    const isOptionSelected = useCallback(
        (option: InternalSelectOption) => {
            const optionId = getOptionIdentifier(option, optionKeyProp);

            if (mode === 'multiple') {
                if (option.key === SELECT_ALL_OPTION_KEY) {
                    return areAllSelected;
                }

                return Array.isArray(value) ? value.includes(String(optionId)) : false;
            }

            return String(value) === String(optionId);
        },
        [value, mode, areAllSelected, optionKeyProp]
    );

    const selectedOption = useMemo(() => {
        if (value === undefined || value === null) {
            return null;
        }

        return (
            options.flattenedParsedOptions.find(
                (opt) => String(value) === String(getOptionIdentifier(opt, optionKeyProp))
            ) ?? null
        );
    }, [value, options.flattenedParsedOptions, optionKeyProp]);

    return useMemo(
        () => ({
            selectedOption,
            handleSelectAll,
            mergedOnChange,
            selectOptionByIndex,
            handleSelect,
            isOptionSelected
        }),
        [
            handleSelectAll,
            mergedOnChange,
            selectOptionByIndex,
            handleSelect,
            isOptionSelected,
            selectedOption
        ]
    );
};

export type SelectSelectionReturn = ReturnType<typeof useSelectSelection>;
